import React from 'react';

import { Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { useNavigationStack } from '../../../../context/NavigationStackContext';
import type { ICommunitiesViewCommunity } from '../../../../redux/api/communities/communities-api.types';
import { useGetMyDetails } from '../../../../redux/api/users/usersApi';
import { AvatarSize, AvatarTypes } from '../../../../shared/constants';
import { Paths } from '../../../../shared/paths';
import AppCommunityAvatar from '../../../designe/app-entity-avatar/app-community-avatar/AppCommunityAvatar';
import {
  CancelSubscriptionRequestButton,
  RequestSubscriptionButton,
  SubscribeButton,
  UnsubscribeButton,
} from '../../../designe/buttons/subscription-buttons';
import CommunityParticipantsCount from '../CommunityParticipantsCount';
import { useStyles } from './CommunityBigItem.styles';
import { useCommunityBigItem } from './useCommunityBigItem';

interface ICommunityBigItem {
  community: ICommunitiesViewCommunity;
}

export default function CommunityBigItem(props: ICommunityBigItem) {
  const { community } = props;
  const {
    onSubscribe,
    onUnsubscribe,
    isSubscribeLoading,
    isUnsubscribeLoading,
  } = useCommunityBigItem(community);
  const { navigate } = useNavigationStack();
  const { data: user } = useGetMyDetails();
  const isUserOwner = community.ownerId === user?.id;
  const theme = useTheme();
  const { classes } = useStyles(theme);
  const isListIconOnly = useMediaQuery(theme.breakpoints.down('sm'));

  function clickOnCommunityHandler() {
    navigate(`${Paths.communities.root}/${community.id}`);
  }

  return (
    <div className={classes.communityWrapper} onClick={clickOnCommunityHandler}>
      <div className={classes.avatarWrapper}>
        <AppCommunityAvatar
          community={community}
          size={AvatarSize.m}
          type={AvatarTypes.round}
        />
      </div>
      <div className={classes.communityDetails}>
        <div className={classes.infoWrapper}>
          <Typography variant="h3">{community.title}</Typography>
          {community.participantsCount !== undefined && (
            <CommunityParticipantsCount
              count={community.participantsCount}
              variant="list"
            />
          )}
          {!!community.description && (
            <Typography
              variant="label-text-sm"
              className={classes.infoDescription}>
              {community.description}
            </Typography>
          )}
        </div>
        <div
          className={classes.controlWrapper}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}>
          {!isUserOwner && (
            <>
              {community?.permissions?.canSubscribe && (
                <SubscribeButton
                  iconOnly={isListIconOnly}
                  loading={isSubscribeLoading}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onSubscribe(community.id);
                  }}
                />
              )}
              {community?.permissions?.canRequestSubscription && (
                <RequestSubscriptionButton
                  iconOnly={isListIconOnly}
                  loading={isSubscribeLoading}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onSubscribe(community.id);
                  }}
                />
              )}
              {community?.permissions?.canUnsubscribe && (
                <UnsubscribeButton
                  iconOnly={isListIconOnly}
                  loading={isUnsubscribeLoading}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onUnsubscribe(community.id);
                  }}
                />
              )}
              {community?.permissions?.canCancelRequestSubscription && (
                <CancelSubscriptionRequestButton
                  iconOnly={isListIconOnly}
                  loading={isUnsubscribeLoading}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onUnsubscribe(community.id);
                  }}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
